import { Button, makeStyles } from '@material-ui/core';
import useChatActions from '@src/hooks/useChatActions';
import { ChatStatus } from '@src/model/frontendmodel';
import { fetchWhatsAppTemplatesByContactId } from '@src/reducers/whatsAppTemplates';
import { activeChatSelector } from '@src/selectors/chats';
import {
  selectDefaultWhatsAppTemplateByContactId,
  selectWhatsAppTemplatesWithoutCallType,
} from '@src/selectors/whatsAppTemplates';
import { RootState } from '@src/store/reduxstore';
import { TemplatedMsgWrapper } from '@src/types';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import TemplatedMsgSelectionDialog from '../TemplatedMsg/TemplatedMsgSelectionDialog';

export interface ResumeButtonProps {
  display: boolean;
  label: string;
  onClick: () => void;
}

const useStyles = makeStyles(() => ({
  root: {
    borderRadius: '24px',
    textTransform: 'capitalize',
    whiteSpace: 'nowrap',
  },
}));

const ResumeButtonWrapper: React.FC = () => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const chatActions = useChatActions();

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<TemplatedMsgWrapper | null>(null);

  const contactTemplates = useSelector(selectWhatsAppTemplatesWithoutCallType);
  const defaultTemplate = useSelector(selectDefaultWhatsAppTemplateByContactId);

  const activeChat = useSelector((state: RootState) =>
    activeChatSelector(state)
  );

  const displayResumeButton = !!(
    activeChat &&
    activeChat?.hasPassedMaxTime &&
    defaultTemplate &&
    activeChat.status !== ChatStatus.BOT_CHATTING
  );

  const handleRequestResumeChat = () => {
    if (!selectedTemplate) {
      // eslint-disable-next-line no-console
      console.error('No template selected');
      return;
    }

    chatActions.resumeChat(
      activeChat.id,
      String(selectedTemplate.templatedMsg.id)
    );
    setOpenDialog(false);
  };

  const handleResumeButtonClick = () => {
    setOpenDialog(true);
    dispatch(fetchWhatsAppTemplatesByContactId());
  };

  useEffect(() => {
    if (defaultTemplate) {
      setSelectedTemplate(defaultTemplate);
    }
  }, [defaultTemplate]);

  useEffect(() => {
    dispatch(fetchWhatsAppTemplatesByContactId());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <ResumeButton
        onClick={handleResumeButtonClick}
        display={displayResumeButton}
        label={formatMessage({ id: 'AgentToolbar.resumeButton.label' })}
      />

      <TemplatedMsgSelectionDialog
        open={openDialog}
        templatedMsgs={contactTemplates}
        selectedTemplate={selectedTemplate}
        handleTemplatedMsgSelection={setSelectedTemplate}
        handleOk={() => setOpenDialog(false)}
        requestResumeChat={handleRequestResumeChat}
      />
    </>
  );
};

export const ResumeButton: React.FC<ResumeButtonProps> = ({
  onClick,
  label,
  display,
}) => {
  const { root } = useStyles();

  if (!display) {
    return null;
  }

  return (
    <Button
      variant="contained"
      color="primary"
      onClick={onClick}
      className={root}
    >
      {label}
    </Button>
  );
};

export default ResumeButtonWrapper;
